import { apiClient } from '../lib/apiClient';

/** Raw measured values keyed by the lab's parameter key (e.g. `{ Ca: 430, Mg: 1310 }`). */
export type IcpResults = Record<string, number | string | null>;

/** Unit per results key, as derived by the backend from the stored key (e.g. `{ Ca: 'mg/L' }`). */
export type IcpUnits = Record<string, string>;

/**
 * One ICP analysis as returned by `GET /api/v1/aquariums/:id/icp-analyses`
 * (list items) and `GET …/icp-analyses/:analysisId` (detail, adds `sampleId`).
 */
export interface IcpAnalysis {
  id: string;
  aquariumId: string;
  provider: string;
  reportDate: string | null;
  createdAt: string;
  parameterCount: number;
  sampleId?: string | null;
  parseWarning?: boolean;
  results: IcpResults;
  /**
   * Unit of each `results` value, keyed like `results` (e.g. `{ Ca: 'mg/L', Li: 'µg/L', kh: 'dKH' }`).
   * Keys without a known unit (pH, relative density) are absent — show no unit for them.
   */
  units: IcpUnits;
  /**
   * Upload response only: parameters the report marks as below the detection
   * limit (ATI "---", Fauna Marin "n.n." / "< x"). Not stored anywhere.
   */
  belowDetection?: IcpBelowDetection[];
}

/** One parameter below the lab's detection limit (upload response). */
export interface IcpBelowDetection {
  /** results key, e.g. "Mn", "NO3". */
  key: string;
  /** The lab's own label, e.g. "Mangan". */
  providerParameterName?: string;
  /** Detection limit, when the report states one ("< 0,5"). */
  limit?: number;
  unit?: string;
}

/** Keeps only well-formed entries of an upload response's `belowDetection`. */
export function toBelowDetection(raw: unknown): IcpBelowDetection[] {
  if (!Array.isArray(raw)) return [];
  const out: IcpBelowDetection[] = [];
  for (const item of raw as unknown[]) {
    if (typeof item !== 'object' || item === null) continue;
    const entry = item as Record<string, unknown>;
    if (typeof entry.key !== 'string' || entry.key === '') continue;
    out.push({
      key: entry.key,
      ...(typeof entry.providerParameterName === 'string' ? { providerParameterName: entry.providerParameterName } : {}),
      ...(typeof entry.limit === 'number' && Number.isFinite(entry.limit) ? { limit: entry.limit } : {}),
      ...(typeof entry.unit === 'string' && entry.unit !== '' ? { unit: entry.unit } : {}),
    });
  }
  return out;
}

/** One page of `GET /api/v1/aquariums/:id/icp-analyses`. */
export interface IcpAnalysisListPage {
  analyses: IcpAnalysis[];
  total: number;
  limit: number;
  offset: number;
}

/** Page size of the ICP analysis list (backend default and max are 20 / 100). */
export const ICP_PAGE_SIZE = 20;

type RawIcpAnalysis = Partial<Omit<IcpAnalysis, 'results' | 'units' | 'belowDetection'>> & {
  id: string;
  providerName?: string | null;
  results?: unknown;
  units?: unknown;
  belowDetection?: unknown;
};

function toResults(raw: unknown): IcpResults {
  return typeof raw === 'object' && raw !== null && !Array.isArray(raw) ? (raw as IcpResults) : {};
}

/** Keeps only non-empty string units; anything else means "no known unit". */
export function toUnits(raw: unknown): IcpUnits {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return {};
  const units: IcpUnits = {};
  for (const [key, unit] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof unit === 'string' && unit.trim() !== '') units[key] = unit.trim();
  }
  return units;
}

/** Normalises one backend analysis row; tolerates a legacy `providerName` field. */
export function normalizeIcpAnalysis(raw: RawIcpAnalysis, aquariumId: string): IcpAnalysis {
  const results = toResults(raw.results);
  return {
    id: String(raw.id),
    aquariumId: raw.aquariumId ?? aquariumId,
    provider: raw.provider ?? raw.providerName ?? '',
    reportDate: raw.reportDate ?? null,
    createdAt: raw.createdAt ?? '',
    parameterCount:
      typeof raw.parameterCount === 'number' ? raw.parameterCount : Object.keys(results).length,
    sampleId: raw.sampleId ?? null,
    results,
    units: toUnits(raw.units),
  };
}

/**
 * Lists one page of an aquarium's ICP analyses (newest first). The API answers
 * `{ data: { analyses, total, limit, offset } }`; a bare array in `data` is
 * tolerated and treated as one complete page.
 */
export async function listAnalyses(
  aquariumId: string,
  params?: { limit?: number; offset?: number },
): Promise<IcpAnalysisListPage> {
  const query = new URLSearchParams();
  if (params?.limit != null) query.set('limit', String(params.limit));
  if (params?.offset != null) query.set('offset', String(params.offset));
  const qs = query.toString();
  const response = await apiClient.get<{ data?: RawIcpAnalysis[] | Partial<IcpAnalysisListPage> | null }>(
    `/api/v1/aquariums/${aquariumId}/icp-analyses${qs ? `?${qs}` : ''}`,
  );
  const payload = response?.data ?? null;
  if (Array.isArray(payload)) {
    const analyses = payload.map((raw) => normalizeIcpAnalysis(raw, aquariumId));
    const offset = params?.offset ?? 0;
    return { analyses, total: offset + analyses.length, limit: analyses.length, offset };
  }
  const rawList = Array.isArray(payload?.analyses) ? (payload.analyses as RawIcpAnalysis[]) : [];
  const analyses = rawList.map((raw) => normalizeIcpAnalysis(raw, aquariumId));
  const offset = typeof payload?.offset === 'number' ? payload.offset : (params?.offset ?? 0);
  return {
    analyses,
    total: typeof payload?.total === 'number' ? payload.total : offset + analyses.length,
    limit: typeof payload?.limit === 'number' ? payload.limit : (params?.limit ?? analyses.length),
    offset,
  };
}

export async function deleteAnalysis(aquariumId: string, analysisId: string): Promise<void> {
  await apiClient.delete(`/api/v1/aquariums/${aquariumId}/icp-analyses/${analysisId}`);
}

export async function uploadIcpAnalysis(
  file: File,
  aquariumId: string,
  providerId: string,
): Promise<IcpAnalysis> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('provider_id', providerId);
  const response = await apiClient.post<{ data: RawIcpAnalysis }>(
    `/api/v1/aquariums/${aquariumId}/icp-analyses/upload`,
    formData,
  );
  return {
    ...normalizeIcpAnalysis(response.data, aquariumId),
    parseWarning: response.data.parseWarning === true,
    belowDetection: toBelowDetection(response.data.belowDetection),
  };
}

export async function getAnalysis(aquariumId: string, analysisId: string): Promise<IcpAnalysis> {
  const response = await apiClient.get<{ data: RawIcpAnalysis }>(
    `/api/v1/aquariums/${aquariumId}/icp-analyses/${analysisId}`,
  );
  return normalizeIcpAnalysis(response.data, aquariumId);
}

export type FaunaMarinSyncErrorCode =
  | 'PROVIDER_AUTH_FAILED'
  | 'PROVIDER_NOT_CONFIGURED'
  | 'PROVIDER_NO_AQUARIUM'
  | 'PROVIDER_RATE_LIMITED'
  | 'PROVIDER_PARSE_FAILED'
  | 'PROVIDER_NETWORK_ERROR';

/**
 * SSE events of POST /api/v1/aquariums/:aquariumId/icp-analyses/sync/fauna-marin
 * (Fauna Marin Lab API v2). Error events carry codes only — never raw text.
 */
export type FaunaMarinSyncEvent =
  | { type: 'authenticated'; account: { displayName: string }; aquariumName: string }
  | { type: 'list'; total: number; toImport: number; skipped: number; startdate: string | null }
  | { type: 'analysis_done'; id: string; date: string; dbId: string; parametersFound: number; reportDate: string | null; action: 'imported' | 'updated' }
  | { type: 'analysis_failed'; id: string; date: string; phase: 'map' | 'save' }
  // Whole-sync abort — never tied to a single analysis.
  | { type: 'sync_failed'; code: FaunaMarinSyncErrorCode }
  | { type: 'done'; imported: number; updated: number; skipped: number; failed: number; partial: boolean; aborted: boolean };

/**
 * Stream the Fauna Marin sync with the user's saved API token. Fires onEvent
 * for each SSE event as it arrives. Throws ApiError for errors before the
 * stream starts (no token, token rejected, rate limit, interface not enabled …).
 *
 * `faunaMarinAquariumId` is the Fauna Marin aquarium (from the verify call) to
 * import from; null lets the backend use the stored mapping.
 */
export async function syncFaunaMarinAnalyses(
  aquariumId: string,
  faunaMarinAquariumId: string | null,
  onEvent: (event: FaunaMarinSyncEvent) => void,
): Promise<void> {
  const body: Record<string, string> = faunaMarinAquariumId ? { faunaMarinAquariumId } : {};
  const response = await apiClient.streamPost(
    `/api/v1/aquariums/${aquariumId}/icp-analyses/sync/fauna-marin`,
    body,
  );

  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // SSE frames are separated by \n\n
    const frames = buffer.split('\n\n');
    buffer = frames.pop() ?? '';

    for (const frame of frames) {
      const dataLine = frame.split('\n').find((l) => l.startsWith('data: '));
      if (!dataLine) continue;
      const event = JSON.parse(dataLine.slice(6)) as FaunaMarinSyncEvent;
      onEvent(event);
    }
  }
}
